import {
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import moment, { Moment } from 'moment';
import { Approval, Document, DocumentFilters } from '../../../models/document';
import { SelectionModel } from '@angular/cdk/collections';
import { DocumentService } from 'src/app/services/document.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { CdkMenuTrigger } from '@angular/cdk/menu';
import { User } from 'src/app/models/user';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { ToastrService } from 'ngx-toastr';
import {
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
  from,
  Subject,
} from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';
import { MessageService } from 'src/app/services/message.service';
import {
  DocumentsReportComponent,
} from 'src/app/components/documents-display/documents-report/documents-report.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import {
  ConfirmationDialogComponent,
} from 'src/app/dialogs/confirmation-dialog/confirmation-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import {
  Level,
} from '../../../components/documents-filters-drawer/documents-filter-drawer.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OrganizationService } from 'src/app/services/organization.service';
import { OtpService } from '../../../services/otp.service';
import {
  numberArray,
  numberArraySerializer,
  optionalString,
  optionalStringSerializer,
  QueryParamManager,
  stringArray,
  stringArraySerializer,
} from 'src/app/helpers/query-params.helper';
import { TrademarkService } from 'src/app/services/trademark.service';
import {
  TrademarkFamilyService,
} from 'src/app/services/trademark-family.service';
import { TerritoryService } from '../../../services/territory.service';
import { Territory } from '../../../models/territory';
import { DocumentClass } from '../../../models/documentClass';
import { TrademarkFamily } from '../../../models/trademarkFamily';
import { Trademark } from '../../../models/trademark';
import { ProductService } from '../../../models/productService';
import { Tag } from '../../../models/tag';
import { TagService } from '../../../services/tag.service';
import {
  ProductServiceService
} from '../../../services/product-service.service';

// url serializable filters for query params. only store entities' ids
type DocumentQueryParams = {
  query?: string;
  approval: string[];
  classNumber: number[];
  territories: string[];
  documentClass: string[];
  trademark: string[];         // _id values
  trademarkFamily: string[];   // _id values
  productsServices: string[];  // _id values
  nonUse?: string;             // 'true' | 'false' | undefined
  periodStart?: string;        // ISO string
  periodEnd?: string;          // ISO string
  tags: string[];              // _id values
}

@Component({
  selector: 'app-documents-list',
  templateUrl: './documents-list.component.html',
  styleUrls: ['./documents-list.component.scss']
})
export class DocumentsListComponent implements OnInit {
  loading: boolean = false;
  done: boolean = false;
  noDocs: boolean = false;

  count: number = 0;

  filteredDocuments: Document[] = [];
  selection = new SelectionModel<Document>(true, []);

  public readonly filtersSubject = new Subject<DocumentFilters>();

  //Query param manager for persisting document filters in the URL.
  // store entity fields as ID arrays and resolves them on load
  readonly documentManager = new QueryParamManager<DocumentQueryParams, Document>(
    {
      defaultLimit: 10,
      defaultSort: '-createdAt',
      fields: {
        query: {
          paramKey: 'query',
          deserialize: optionalString,
          serialize: optionalStringSerializer,
        },
        approval: {
          paramKey: 'approval',
          deserialize: stringArray,
          serialize: stringArraySerializer,
        },
        classNumber: {
          paramKey: 'classNumber',
          deserialize: numberArray,
          serialize: numberArraySerializer,
        },
        territories: {
          paramKey: 'territories',
          deserialize: stringArray,
          serialize: stringArraySerializer,
        },
        documentClass: {
          paramKey: 'documentClass',
          deserialize: stringArray,
          serialize: stringArraySerializer,
        },
        trademark: {
          paramKey: 'trademark',
          deserialize: stringArray,
          serialize: stringArraySerializer,
        },
        trademarkFamily: {
          paramKey: 'trademarkFamily',
          deserialize: stringArray,
          serialize: stringArraySerializer,
        },
        productsServices: {
          paramKey: 'productsServices',
          deserialize: stringArray,
          serialize: stringArraySerializer,
        },
        nonUse: {
          paramKey: 'nonUse',
          deserialize: optionalString,
          serialize: optionalStringSerializer,
        },
        periodStart: {
          paramKey: 'periodStart',
          deserialize: optionalString,
          serialize: optionalStringSerializer,
        },
        periodEnd: {
          paramKey: 'periodEnd',
          deserialize: optionalString,
          serialize: optionalStringSerializer,
        },
        tags: {
          paramKey: 'tags',
          deserialize: stringArray,
          serialize: stringArraySerializer,
        },
      },
    },
    this.router,
  );

  @ViewChild("documentsReport") documentsReport?: DocumentsReportComponent;

  classes: any[] = [];

  user?: User;

  // for expanding alternate codes in API calls
  private territoryById = new Map<string, Territory>();
  private territoriesLoaded = false;

  // caches so we don't re-fetch entities when their ID list hasn't changed
  private lastResolvedTrademarkIds: string[] = [];
  private lastResolvedTrademarkFamilyIds: string[] = [];
  private lastResolvedTagsIds: string[] = [];
  private lastProductsAndServicesIds: string[] = [];

  displayMode: string = "table";

  levels: Level = {
    1: 'trademark',
    2: 'territory',
    3: 'nice'
  };

  active: boolean = false;

  noDocumentButton: {
    label: string;
    icon: string;
    class: string;
    action: Function
  } = {
      label: 'ACTIONS.UPLOAD',
      icon: 'cloud_upload',
      class: 'main-button',
      action: () => { this.documentService.openUploadDialog([]) }
    }

  @ViewChild('trigger') trigger?: CdkMenuTrigger;
  @ViewChild('documentscontainer', { read: ElementRef }) documentsContainer?: ElementRef;

  private readonly pagerSubject = new Subject<PageEvent>();

  constructor(
    private readonly _message: MessageService,
    private readonly authService: AuthenticationService,
    private readonly dialog: MatDialog,
    public documentService: DocumentService,
    private readonly organizationService: OrganizationService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService,
    private readonly otpService: OtpService,
    private readonly trademarkService: TrademarkService,
    private readonly trademarkFamilyService: TrademarkFamilyService,
    private readonly territoryService: TerritoryService,
    private readonly tagService: TagService,
    private readonly productAndServicesService: ProductServiceService,
    private readonly destroyRef: DestroyRef,
  ) {
    this.authService.user.subscribe((user: User | undefined) => {
      this.user = user;
    });
    this.pagerSubject
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntilDestroyed()
      ).subscribe(async () => {
        await this.retrieveDocuments(false);
      });
  }

  async ngOnInit() {
    // Load territory index once, before we start reacting to query params,
    // so it's guaranteed ready and isn't retried on every param change.
    await this.loadTerritories();

    // Subscribe to query params to initialize filter state from URL.
    // switchMap cancels/drops any in-flight async work from a previous
    // emission the moment a newer one arrives, preventing state races.
    let isFirstLoad = true;
    this.route.queryParams
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((params) => from(
          this.handleQueryParams(
            params,
            isFirstLoad,
          ),
        )),
      )
      .subscribe(() => {
        isFirstLoad = false;
      });
  }

  private async loadTerritories(): Promise<void> {
    if (this.territoriesLoaded) return;
    try {
      this.territoryById = new Map(
        (await this.territoryService.retrieve()).map((t) => [t.id, t]),
      );
      this.territoriesLoaded = true;
    } catch (err) {
      console.error(
        'Failed to load territories',
        err,
      );
      this.toastr.error('ERRORS.GENERIC');
    }
  }

  private async handleQueryParams(
    params: Params,
    isFirstLoad: boolean
  ): Promise<void> {
    this.documentManager.fromParams(params);

    // Apply URL filter state to the service (with entity ID resolution)
    await this.applyManagerFiltersToService();

    if (isFirstLoad) {
      this.loading = true;
      this.done = false;
      await this.retrieveDocuments(true);
      this.loading = false;
      this.done = true;
    }
  }

  async onFilterChange() {
    // Sync current filter state (full objects in service) to URL
    this.syncServiceFiltersToManager();
    this.resetPage();
    await this.applyAllQueryParams();

    if (this.displayMode === 'report' && this.documentsReport) {
      await this.countDocuments();
      await this.documentsReport.aggregateDocuments();
    } else {
      await this.retrieveDocuments(false);
    }
  }

  async reload() {
    this.resetPage();
    if (this.displayMode === 'report' && this.documentsReport) {
      await this.countDocuments();
      await this.documentsReport.aggregateDocuments();
    } else {
      await this.retrieveDocuments(false);
    }
  }

  async onTabChange(event: any) {
    if (event.index === 0) {
      this.displayMode = "table";
    } else if (event.index === 1) {
      this.displayMode = "report";
    } else if (event.index === 2) {
      this.displayMode = "hierarchy";
    }
    if (this.displayMode === "table") {
      await this.retrieveDocuments(false);
    }
  }

  async countDocuments() {
    try {
      this.count = (await this.documentService.countAll(
        this.getExpandedTerritories(this.documentService.filters.territories),
        this.documentService.filters.classNumber,
        this.documentService.filters.productsServices,
        this.documentService.filters.trademark,
        this.documentService.filters.trademarkFamily,
        this.documentService.filters.documentClass,
        this.documentService.filters.period,
        this.documentService.filters.approval,
        this.documentService.filters.tags,
        this.documentService.filters.query,
        this.documentService.filters.isConfidential)).count;
    } catch (err) {
      this.toastr.error(`ERRORS.GENERIC`)
    }
  }


  async retrieveDocuments(init: boolean) {
    try {
      this._message.emitChange("LOADING", "START");

      this.documentService.pager = {
        ...this.documentService.pager,
        ...await this.documentService.retrieveAll(
          this.documentService.pager.page,
          this.documentService.pager.limit,
          this.documentService.pager.offset,
          this.documentService.pager.sort,
          this.getExpandedTerritories(this.documentService.filters.territories),
          this.documentService.filters.classNumber,
          this.documentService.filters.productsServices,
          this.documentService.filters.trademark,
          this.documentService.filters.trademarkFamily,
          this.documentService.filters.documentClass,
          this.documentService.filters.period,
          this.documentService.filters.nonUse,
          this.documentService.filters.approval,
          this.documentService.filters.tags,
          this.documentService.filters.query,
          this.documentService.filters.isConfidential
        )
      };
      this.filteredDocuments = this.documentService.pager.docs;
      this.count = this.documentService.pager.totalDocs;

      // "true empty" only applies when no filters are responsible for the zero count
      this.noDocs = init && this.count === 0 && !this.documentManager.hasActiveFilters();

      this._message.emitChange("LOADING", "END");
    } catch (err) {
      this._message.emitChange("LOADING", "END");
      this.toastr.error(`ERRORS.GENERIC`)
    }
  }

  resetPage() {
    this.documentManager.resetPage();
    this.documentManager.pager.limit = 10;

    this.documentService.pager.limit = 10;
    this.documentService.pager.page = 1;
    this.documentService.pager.offset = 0;
  }

  async handlePage(event: PageEvent) {
    this.documentManager.handlePage(event);
    this.documentService.pager.limit = this.documentManager.pager.limit;
    this.documentService.pager.page = this.documentManager.pager.page;
    this.documentService.pager.offset = this.documentManager.pager.offset;
    await this.applyAllQueryParams();
    this.pagerSubject.next(event);
  }

  onSortChange(event: any) {
    if (['asc', 'desc'].includes(event.direction)) {
      this.documentManager.onSortChange(event);
      this.documentService.pager.sort = this.documentManager.pager.sort;
      this.applyAllQueryParams();
      this.retrieveDocuments(false);
    }
  }

  // region Query param helpers

  // push current manager state to url
  private async applyAllQueryParams(): Promise<void> {
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.documentManager.toParams(),
    });
  }

  //save current service-level filter values (full entity objects) onto the QueryParamManager
  private syncServiceFiltersToManager(): void {
    const docService = this.documentService.filters;

    this.documentManager.filters.query = docService.query;
    this.documentManager.filters.approval = docService.approval;
    this.documentManager.filters.classNumber = docService.classNumber;
    // only store main territory IDs in query params, not alternate codes
    this.documentManager.filters.territories = this.filterToMainTerritoryIds(
      docService.territories
    );
    this.documentManager.filters.documentClass = docService.documentClass;
    this.documentManager.filters.trademark = docService.trademark.map((t) => t._id!)
      .filter(Boolean);
    this.documentManager.filters.trademarkFamily = docService.trademarkFamily.map(
      (f) => f._id!,
    )
      .filter(Boolean);
    this.documentManager.filters.productsServices = docService.productsServices.map(
      (p) => p._id!,
    )
      .filter(Boolean);

    this.documentManager.filters.nonUse = docService.nonUse === undefined
      ? undefined
      : String(docService.nonUse);

    this.documentManager.filters.periodStart = docService.period.start?.toISOString(
      true
    );
    this.documentManager.filters.periodEnd = docService.period.end?.toISOString(
      true
    );

    this.documentManager.filters.tags = docService.tags.map((t) => t._id!)
      .filter(Boolean);
  }

  // apply the manager's filter state to `documentService.filters` and resolving entity IDs to full objects when possible
  private async applyManagerFiltersToService(): Promise<void> {
    const docManager = this.documentManager.filters;
    const docService = this.documentService.filters;

    docService.query = docManager.query;
    docService.approval = docManager.approval.map(approval => this.approvalStringToApproval(
      approval,
    ))
      .filter((a): a is Approval => !!a);
    docService.classNumber = docManager.classNumber;
    docService.territories = docManager.territories;
    docService.documentClass = docManager.documentClass
      .map((dc) => this.documentClassStringToDocumentClass(dc))
      .filter((dc): dc is DocumentClass => !!dc);

    // resolve trademark IDs to objects (skip refetch if ID set is unchanged)
    if (docManager.trademark.length > 0) {
      if (!this.sameIds(docManager.trademark, this.lastResolvedTrademarkIds)) {
        try {
          docService.trademark = await this.trademarkService.retrieveByIds(
            docManager.trademark
          );
        } catch (err) {
          console.error('Failed to resolve trademarks from query params', err);
          docService.trademark = docManager.trademark.map((id) => ({ _id: id } as Trademark));
          this.toastr.error('ERRORS.GENERIC');
        }
        this.lastResolvedTrademarkIds = docManager.trademark;
      }
    } else {
      docService.trademark = [];
      this.lastResolvedTrademarkIds = [];
    }

    // resolve trademark family IDs to objects (skip refetch if ID set is unchanged)
    if (docManager.trademarkFamily.length > 0) {
      if (!this.sameIds(docManager.trademarkFamily, this.lastResolvedTrademarkFamilyIds)) {
        try {
          docService.trademarkFamily = await this.trademarkFamilyService.retrieveByIds(
            docManager.trademarkFamily
          );
        } catch (err) {
          console.error('Failed to resolve trademark families from query params', err);
          docService.trademarkFamily = docManager.trademarkFamily.map(
            (id) => ({ _id: id } as TrademarkFamily)
          );
          this.toastr.error('ERRORS.GENERIC');
        }
        this.lastResolvedTrademarkFamilyIds = docManager.trademarkFamily;
      }
    } else {
      docService.trademarkFamily = [];
      this.lastResolvedTrademarkFamilyIds = [];
    }

    // resolve products services (id only)
    if (docManager.productsServices.length > 0) {
      if (!this.sameIds(docManager.productsServices, this.lastProductsAndServicesIds)) {
        try {
          docService.productsServices = await this.productAndServicesService.retrieveByIds(
            docManager.productsServices
          );
        } catch (err) {
          console.error('Failed to resolve products and services from query params', err);
          docService.productsServices = docManager.productsServices.map((id) => ({ _id: id } as ProductService));
          this.toastr.error('ERRORS.GENERIC');
        }
        this.lastProductsAndServicesIds = docManager.productsServices;
      }
    } else {
      docService.productsServices = [];
      this.lastProductsAndServicesIds = [];
    }

    // non-use flag
    docService.nonUse = docManager.nonUse === 'true'
      ? true
      : docManager.nonUse === 'false'
        ? false
        : undefined;

    // period from ISO strings
    docService.period = {
      start: docManager.periodStart
        ? this.parseMoment(docManager.periodStart)
        : undefined,
      end: docManager.periodEnd
        ? this.parseMoment(docManager.periodEnd)
        : undefined,
    };

    // tags (id only)
    if ( docManager.tags.length > 0) {
      if (!this.sameIds(docManager.tags, this.lastResolvedTagsIds)) {
        try {
          docService.tags = await this.tagService.retrieveByIds(
            docManager.tags
          );
        } catch (err) {
          console.error('Failed to resolve tags from query params', err);
          docService.tags = docManager.tags.map(
            (id) => ({ _id: id } as Tag)
          );
          this.toastr.error('ERRORS.GENERIC');
        }
        this.lastResolvedTagsIds = docManager.tags;
      }
    } else {
      docService.tags = [];
      this.lastResolvedTagsIds = [];
    }
  }

  private parseMoment(value: string): Moment | undefined {
    const m = moment(value, moment.ISO_8601, true);
    return m.isValid() ? m : undefined;
  }

  private sameIds(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((id, i) => id === b[i]);
  }

  private approvalStringToApproval(value: string): Approval | undefined {
    switch (value) {
      case 'approved':
        return Approval.APPROVED;
      case 'pending':
        return Approval.PENDING;
      case 'rejected':
        return Approval.REJECTED;
      default:
        return undefined;
    }
  }

  private documentClassStringToDocumentClass(value: string): DocumentClass | undefined {
    switch (value) {
      case 'AUTOMATIC':
        return DocumentClass.AUTOMATIC;
      case 'INVOICE':
        return DocumentClass.INVOICE;
      case 'CERTIFICATION':
        return DocumentClass.CERTIFICATION;
      case 'CONTRACT':
        return DocumentClass.CONTRACT;
      case 'PRESS_EXTRACT':
        return DocumentClass.PRESS_EXTRACT;
      case 'BOOKLET':
        return DocumentClass.BOOKLET;
      case 'CATALOG':
        return DocumentClass.CATALOG;
      case 'PICTURE':
        return DocumentClass.PICTURE;
      case 'ADVERTISING':
        return DocumentClass.ADVERTISING;
      case 'WEBSITE_SCREENSHOT':
        return DocumentClass.WEBSITE_SCREENSHOT;
      case 'ONLINE_STORE_SCREENSHOT':
        return DocumentClass.ONLINE_STORE_SCREENSHOT;
      case 'ACCOUNTING_DOCUMENT':
        return DocumentClass.ACCOUNTING_DOCUMENT;
      case 'EMAIL':
        return DocumentClass.EMAIL;
      case 'AWARD':
        return DocumentClass.AWARD;
      case 'OTHER':
        return DocumentClass.OTHER;
      default:
        return undefined;
    }
  }

  // territory ID + alternate codes
  private getExpandedTerritories(ids: string[]): string[] {
    if (!ids?.length || !this.territoryById.size) return ids || [];
    return ids.flatMap((id) => {
      const t = this.territoryById.get(id);
      if (!t) return [id];
      return t.alternateCodes?.length
        ? [t.id, ...t.alternateCodes]
        : [t.id];
    });
  }

  // strip alternate codes from a territory ID list
  private filterToMainTerritoryIds(ids: string[]): string[] {
    if (!ids?.length || !this.territoryById.size) return ids || [];
    return ids.filter((id) => this.territoryById.has(id));
  }

  // endregion

  upload() {
    this.documentService.openUploadDialog([]);
  }

  toBatches() {
    this.router.navigateByUrl('/documents/batches', {
      state: {
        from: "DOCUMENTS"
      }
    })
  }


  async deleteDocuments() {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: 'DOCUMENTS_LIST.DELETE_DOCUMENTS_TITLE',
        text: this.translate.instant('DOCUMENTS_LIST.DELETE_DOCUMENTS_TEXT', { length: this.selection.selected.length }),
        button: {
          text: 'ACTIONS.DELETE',
          class: 'danger-button'
        }
      }
    }
    const dialog = this.dialog.open(ConfirmationDialogComponent, config);
    const result: { confirmed: boolean } = await firstValueFrom(dialog.afterClosed());
    if (result && result.confirmed) {
      try {
        this.loading = true;
        this._message.emitChange("LOADING", "START");
        await this.documentService.deleteMany(this.selection.selected.map((doc) => doc._id!));
        this.loading = false;
        this._message.emitChange("LOADING", "END");
        this.toastr.success(this.translate.instant('DOCUMENTS_LIST.DOCUMENTS_DELETED', { length: this.selection.selected.length }));
        this.selection.clear();
        await this.retrieveDocuments(false);


      } catch (err) {
        this.loading = false;
        this._message.emitChange("LOADING", "END");
        this.toastr.error('ERRORS.GENERIC');
      }
    }
  }

  async downloadDocuments() {
    try {
      this.loading = true;
      this._message.emitChange('LOADING', 'START');
      // check if some docs are confidential
      const someConfidentialDocs = this.selection.selected.some(doc => doc.confidential);
      let sca: boolean = this.authService.getStrongAccessToken() !== undefined;

      // ask for otp
      if (someConfidentialDocs && !sca) {
        // check if SAT is already set, request for otp otherwise
        const success = await this.checkFor2FA();

        // user did not complete/pass verification, do nothing
        if (!success) {
          this.loading = false;
          this._message.emitChange('LOADING', 'END');
          this.toastr.error('ERRORS.403_TEXT');
          return;
        }
        sca = true;
      }

      // notify user download started
      this.toastr.info(
        this.translate.instant('DOCUMENTS_LIST.DOCUMENTS_DOWNLOAD_IN_PROGRESS'),
      );

      const blob = await this.documentService.downloadBatch(
        this.selection.selected.map((doc) => doc._id!),
        sca
      );

      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = `uzip-documents-download-${Date.now()}.zip`;
      a.click();
      URL.revokeObjectURL(objectUrl);

      this.loading = false;
      this._message.emitChange('LOADING', 'END');
      this.toastr.success(
        this.translate.instant('DOCUMENTS_LIST.DOCUMENTS_DOWNLOADED', {
          length: this.selection.selected.length,
        }),
      );
    } catch (err) {
      console.log(err);
      this.loading = false;
      this._message.emitChange('LOADING', 'END');
      this.toastr.error(this.translate.instant('ERRORS.GENERIC'));
    }
  }

  private async checkFor2FA(): Promise<boolean> {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: 'DOCUMENTS_LIST.ONE_OR_MORE_DOCUMENTS_REQUIRE_AUTHENTICATION',
        text: 'DOCUMENT.PROTECTED_NEED_OTP',
        button: {
          text: 'ACTIONS.AUTHENTICATE',
          class: 'main-button'
        }
      }
    };

    const dialog = this.dialog.open(ConfirmationDialogComponent, config);
    const result: { confirmed: boolean } = await firstValueFrom(dialog.afterClosed());

    return new Promise((resolve) => {
      if (!result?.confirmed) {
        resolve(false);
        return;
      }

      this.otpService.check(
        () => resolve(true),
        () => resolve(false),
      );
    });
  }
}


